import { useMemo } from 'react';

import { useParams } from 'react-router';

import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosRequestHeaders } from 'axios';
import { ProjectCategory } from 'views/Projects/constants';

import { ExternalImageData } from 'components/UploadImage/UploadImageModal/constants';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export default function useImages(currentPublisherId?: string, uploadContext?: ProjectCategory) {
  const axios = useAxios();
  const { portalId } = useParams();

  const apiRoutes = useMemo(
    () => ({
      images: getApiRouteOrMock(EApiRoutes.IMAGES_V2),
      deleteImage: getApiRouteOrMock(EApiRoutes.DELETE_IMAGES_V2),
      addImage: getApiRouteOrMock(EApiRoutes.POST_IMAGE_V2)
    }),
    []
  );

  const customHeaders: AxiosRequestHeaders | undefined = useMemo(() => {
    if (currentPublisherId && uploadContext === ProjectCategory.PORTAL) {
      return { 'x-portal': currentPublisherId };
    }

    const headers: Record<string, string> = {};
    if (currentPublisherId) {
      headers['x-publishers'] = currentPublisherId;
    } else if (portalId) {
      headers['x-portal'] = portalId;
    }

    return headers as AxiosRequestHeaders;
  }, [currentPublisherId, portalId, uploadContext]);

  const getImages = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.IMAGES],
    queryFn: async () => {
      return await axios.get(apiRoutes.images, {}, customHeaders);
    },
    enabled: !uploadContext && (!!currentPublisherId || !!portalId)
  });

  const deleteImage = useMutation(async (imageId: string) => {
    return await axios.del(
      `${apiRoutes.deleteImage}/${imageId}`,
      {},
      customHeaders
    );
  });

  const addImage = useMutation(async (newImage: FormData | ExternalImageData) => {
    return await axios.post(apiRoutes.addImage, newImage, customHeaders);
  });

  return {
    addImage,
    getImages,
    deleteImage
  };
}
